import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Calendar, Lock, ShoppingBag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatQuantity } from "@/lib/number";
import { formatOrderNumber } from "@/lib/ordersApi";
import { ApiError } from "@/lib/api";
import { fetchAvailableDates } from "@/lib/deliveryApi";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function Checkout() {
  const navigate = useNavigate();
  const {
    cart,
    account,
    placeOrder,
    deliveryUnits,
    deliveryUnitsStatus,
    selectedDeliveryUnitId,
    setSelectedDeliveryUnit,
  } = useApp();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [period, setPeriod] = useState("Manhã (06h - 10h)");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validated = account?.status === "VALIDADO";
  // O carrinho no store já vem reprecificado pela unidade selecionada (ver useApp): usa direto, sem
  // refazer o preview aqui. A escolha da unidade acontece antes, no seletor do topo — o checkout apenas
  // confirma/troca a unidade já ativa.
  const displayCart = cart;
  const subtotal = validated ? displayCart.reduce((acc, i) => acc + (i.pricePerUnit ?? 0) * i.quantity, 0) : null;
  const unitsLoading = deliveryUnitsStatus !== "ready";
  const selectedUnit = deliveryUnits.find((unit) => unit.id === selectedDeliveryUnitId) ?? null;

  useEffect(() => {
    fetchAvailableDates(20)
      .then((isoStrings) => setAvailableDates(isoStrings.map((iso) => parseISO(iso))))
      .catch(() => {})
      .finally(() => setDatesLoading(false));
  }, []);

  const changeUnit = async (unitId: string) => {
    const previous = selectedDeliveryUnitId;
    const unit = deliveryUnits.find((u) => u.id === unitId);
    await setSelectedDeliveryUnit(unitId);
    if (previous && previous !== unitId && unit) {
      toast.success(`Os preços do carrinho foram atualizados para a unidade ${unit.name}.`);
    }
  };

  const isDateAvailable = (day: Date) =>
    availableDates.some(
      (d) =>
        d.getFullYear() === day.getFullYear() &&
        d.getMonth() === day.getMonth() &&
        d.getDate() === day.getDate(),
    );

  if (cart.length === 0) {
    return (
      <div className="container py-16 text-center">
        <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="font-display text-2xl font-bold">Seu carrinho está vazio</h2>
        <p className="text-muted-foreground mt-2">Adicione itens do catálogo para fazer o pedido.</p>
        <Button variant="hero" className="mt-6" onClick={() => navigate("/catalogo")}>
          Ir para o catálogo
        </Button>
      </div>
    );
  }

  const submit = async () => {
    if (!selectedDate) return toast.error("Selecione uma data de entrega");
    // Obrigatória quando o cliente tem unidades cadastradas; se não tem, segue sem seletor.
    if (deliveryUnits.length > 0 && !selectedDeliveryUnitId) {
      return toast.error("Selecione a unidade de compra");
    }
    setIsSubmitting(true);
    try {
      const deliveryWindow = `${format(selectedDate, "dd/MM/yyyy")} · ${period}`;
      const base = { deliveryWindow, notes, deliveryUnitId: selectedDeliveryUnitId || undefined };
      let order;
      try {
        order = await placeOrder(base);
      } catch (e) {
        // Mudança de preço desde a montagem do carrinho (ex.: virada de vigência): confirma antes de prosseguir.
        if (e instanceof ApiError && e.status === 409) {
          const proceed = window.confirm(`${e.message}\n\nDeseja finalizar com os preços atualizados?`);
          if (!proceed) return;
          order = await placeOrder({ ...base, priceChangeAck: true });
        } else {
          throw e;
        }
      }
      if (!order) return toast.error("Não foi possível criar o pedido");
      const label = formatOrderNumber(order.number, order.id);
      toast.success(validated ? `Pedido ${label} criado!` : `Cotação ${label} enviada para análise!`);
      navigate("/pedidos");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Não foi possível criar o pedido");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container max-w-5xl py-8 space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>

      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {validated ? "Finalizar Pedido" : "Revisar e enviar para cotação"}
        </h1>
        <p className="text-muted-foreground mt-1">
          Confirme os itens, a janela de entrega e adicione observações finais.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="surface-card p-6">
            <h2 className="font-display font-semibold text-lg mb-4">
              Itens do pedido <span className="text-muted-foreground text-sm font-normal">({cart.length})</span>
            </h2>
            <ul className="divide-y divide-border">
              {displayCart.map((item) => (
                <li key={item.id} className="py-3 flex items-center gap-3 first:pt-0 last:pb-0">
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center text-xl shrink-0">
                    {item.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm uppercase tracking-wide">{item.productName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatQuantity(item.quantity)} {item.unit}
                      {item.cut ? ` · ${item.cut}` : ""}
                    </p>
                    {item.notes && (
                      <p className="mt-0.5 text-xs text-amber-700">Obs.: {item.notes}</p>
                    )}
                  </div>
                  {validated && item.pricePerUnit ? (
                    <span className="font-semibold tabular-nums">
                      R$ {(item.pricePerUnit * item.quantity).toFixed(2)}
                    </span>
                  ) : validated ? (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      preço a definir
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Lock className="h-3 w-3" /> sob aprovação
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {(unitsLoading || deliveryUnits.length > 0) && (
            <section className="surface-card p-6 space-y-4">
              <h2 className="font-display font-semibold text-lg">Unidade de compra</h2>
              <div className="space-y-2">
                <Label>Selecione a unidade *</Label>
                {unitsLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-surface px-3 flex items-center text-sm text-muted-foreground">
                    Carregando unidades...
                  </div>
                ) : (
                  <select
                    value={selectedDeliveryUnitId ?? ""}
                    onChange={(e) => void changeUnit(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm"
                  >
                    <option value="" disabled>
                      Selecione a unidade de compra
                    </option>
                    {deliveryUnits.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                        {unit.externalCode ? ` (${unit.externalCode})` : ""}
                      </option>
                    ))}
                  </select>
                )}
                {selectedUnit && (selectedUnit.address || selectedUnit.city) && (
                  <p className="text-xs text-muted-foreground">
                    {[selectedUnit.address, selectedUnit.district, selectedUnit.city]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
                {validated && (
                  <p className="text-xs text-muted-foreground">
                    Os preços abaixo já refletem a unidade de compra selecionada.
                  </p>
                )}
              </div>
            </section>
          )}

          <section className="surface-card p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">Janela de entrega</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data preferida *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={datesLoading}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {datesLoading
                        ? "Carregando datas..."
                        : selectedDate
                        ? format(selectedDate, "dd/MM/yyyy", { locale: ptBR })
                        : "Selecione uma data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DayCalendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => { setSelectedDate(date); setCalendarOpen(false); }}
                      disabled={(day) => !isDateAvailable(day)}
                      initialFocus
                      locale={ptBR}
                      fromDate={new Date()}
                    />
                  </PopoverContent>
                </Popover>
                {availableDates.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Próxima data disponível:{" "}
                    {format(availableDates[0], "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Período</Label>
                {(() => {
                  const PRESETS = ["Manhã (06h - 10h)", "Tarde (13h - 17h)", "Madrugada (00h - 05h)"];
                  const CUSTOM = "__custom__";
                  // Custom = qualquer valor que não esteja nos presets — costuma vir do
                  // próprio usuário descrevendo o horário ideal de receber.
                  const isCustom = !PRESETS.includes(period);
                  return (
                    <>
                      <select
                        value={isCustom ? CUSTOM : period}
                        onChange={(e) => {
                          const next = e.target.value;
                          setPeriod(next === CUSTOM ? "" : next);
                        }}
                        className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm"
                      >
                        {PRESETS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                        <option value={CUSTOM}>Personalizado…</option>
                      </select>
                      {isCustom && (
                        <input
                          type="text"
                          value={period}
                          onChange={(e) => setPeriod(e.target.value)}
                          placeholder="ex.: 08h às 10h ou das 13h às 15h"
                          className="mt-2 h-10 w-full rounded-md border border-input bg-surface px-3 text-sm"
                          autoFocus
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observações gerais</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ex: entregar na portaria de serviço" rows={3} />
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="surface-card p-6 sticky top-20">
            <h2 className="font-display font-semibold text-lg mb-4">Resumo</h2>

            {selectedUnit && (
              <div className="mb-4 pb-4 border-b border-border text-sm">
                <p className="text-muted-foreground text-xs">Unidade de compra</p>
                <p className="font-medium">{selectedUnit.name}</p>
              </div>
            )}

            {validated ? (
              <>
                <div className="space-y-2 pb-4 border-b border-border text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Itens</span>
                    <span className="tabular-nums">R$ {subtotal!.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entrega</span>
                    <span className="text-success">a combinar</span>
                  </div>
                </div>
                <div className="flex justify-between items-baseline pt-4">
                  <span className="font-medium">Total</span>
                  <span className="font-display text-2xl font-bold tabular-nums">R$ {subtotal!.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs space-y-1.5">
                <div className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <Lock className="h-3.5 w-3.5 text-warning" /> Modo cotação
                </div>
                <p className="text-muted-foreground">
                  Sua ficha comercial está em análise. Ao enviar, a equipe Naturexpress retorna com preços e confirmação.
                </p>
              </div>
            )}

            <Button variant="hero" size="lg" className="w-full mt-5" onClick={() => void submit()} disabled={isSubmitting}>
              <CheckCircle2 className="h-4 w-4" />
              {isSubmitting ? "Enviando..." : validated ? "Confirmar pedido" : "Enviar para cotação"}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
